import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { ReactKeycloakProvider, useKeycloak } from '@react-keycloak/web';
import keycloakInstance from './Keycloak';
import { cachePermissions, isAuthorized } from './Permissions';
import LoadingIndicator from 'src/components/common/LoadingIndicator/LoadingIndicator';
import { NO_PERMISSIONS_ERROR_MESSAGE, PAGE_INFO, NO_PERMISSIONS_ERROR_CONTACT_SUPPORT_MESSAGE } from './constants';
import PermissionsError from 'src/components/common/PermissionsError/PermissionsError';
import { useLocation } from 'react-router-dom';
import { PageNames } from './types';

const SecurityProvider = ({ children }: { children: ReactNode }) => {
    return (
        <ReactKeycloakProvider
            authClient={keycloakInstance}
            initOptions={{
                onLoad: 'login-required',
                checkLoginIframe: false,
            }}
        >
            {children}
        </ReactKeycloakProvider>
    );
};

const SecurityRoute = ({
    children,
    reroutePage,
    currentPath,
}: {
    children: ReactNode;
    reroutePage?: PageNames;
    currentPath: string;
}) => {
    const { keycloak } = useKeycloak();
    const { authenticated, tokenParsed } = keycloak;
    const isLoggedIn = authenticated ?? false;
    const userEmail = tokenParsed?.email;
    const isCached = cachePermissions(userEmail);

    // Show loading indicator while waiting for keycloak to get user info
    if (isCached) {
        const userPermissions = sessionStorage.getItem(userEmail);

        if (userPermissions === '[]' || userPermissions === '{}') {
            return (
                <PermissionsError
                    message={NO_PERMISSIONS_ERROR_MESSAGE}
                    supportMessage={NO_PERMISSIONS_ERROR_CONTACT_SUPPORT_MESSAGE}
                />
            );
        }

        return isLoggedIn ? checkAuth(children, userEmail, currentPath, reroutePage) : null;
    } else {
        return (
            <div className="overlay">
                <LoadingIndicator message="Loading..." />
            </div>
        );
    }
};

const checkAuth = (children: ReactNode, userId: string, currentPath: string, reroutePage?: PageNames) => {
    let auth = false;

    // Only enforcing permission checks when reroutePage specified.
    if (!reroutePage || currentPath !== PAGE_INFO[reroutePage].href) {
        return children;
    }

    const reroutePermissions = PAGE_INFO[reroutePage].permissions ?? [];

    for (const permission of reroutePermissions) {
        auth = isAuthorized(userId, permission);
        if (auth) break;
    }

    if (!auth) {
        const storedPermissions = sessionStorage.getItem(userId);

        const adminsPermissions = JSON.parse(storedPermissions ?? '{}');

        for (const pageName of Object.keys(PAGE_INFO)) {
            if (PAGE_INFO[pageName].permissions?.find((permission) => permission in adminsPermissions)) {
                return <Navigate to={PAGE_INFO[pageName].href} />;
            }
        }

        return <PermissionsError />;
    } else {
        return children;
    }
};

export default function SecureRoute({ children, reroutePage }: { children: ReactNode; reroutePage?: PageNames }) {
    const location = useLocation();
    const currentPath = location.pathname;

    return (
        <SecurityProvider>
            <SecurityRoute reroutePage={reroutePage} currentPath={currentPath}>
                {children}
            </SecurityRoute>
        </SecurityProvider>
    );
}
